import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import {
  AssignmentAnswerType,
  collectRehostTasks,
  convertPayload,
  ImportResult,
  parseGetcoursePayload,
} from './getcourseImport';

export interface ImportOptions {
  replaceBlocks: boolean;
  useTitle: boolean;
  createAssignment: boolean;
  assignmentAnswer: AssignmentAnswerType;
}

interface GetcourseImportDialogProps {
  open: boolean;
  hasBlocks: boolean;
  genId: () => string;
  onClose: () => void;
  onImport: (_result: ImportResult, _options: ImportOptions) => void;
}

export const GetcourseImportDialog: React.FC<GetcourseImportDialogProps> = ({
  open,
  hasBlocks,
  genId,
  onClose,
  onImport,
}) => {
  const [raw, setRaw] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [replaceBlocks, setReplaceBlocks] = useState<boolean>(true);
  const [useTitle, setUseTitle] = useState<boolean>(true);
  const [createAssignment, setCreateAssignment] = useState<boolean>(true);
  const [assignmentAnswer, setAssignmentAnswer] =
    useState<AssignmentAnswerType>('free_text');

  const apply = useCallback((text: string) => {
    setRaw(text);
    if (!text.trim()) {
      setResult(null);
      setError('');
      return;
    }
    try {
      const r = convertPayload(parseGetcoursePayload(text), genId);
      setResult(r);
      setError('');
      setUseTitle(Boolean(r.title));
      setCreateAssignment(Boolean(r.assignmentText));
      setAssignmentAnswer(r.assignmentAnswer);
    } catch (e) {
      setResult(null);
      setError(e instanceof Error ? e.message : 'Не удалось разобрать данные');
    }
  }, [genId]);

  const readClipboard = useCallback(async () => {
    try {
      apply(await navigator.clipboard.readText());
    } catch {
      setError('Браузер не дал прочитать буфер — вставьте вручную в поле ниже (Ctrl/Cmd+V).');
    }
  }, [apply]);

  // При открытии сразу пробуем прочитать буфер обмена.
  useEffect(() => {
    if (open) {
      setReplaceBlocks(!hasBlocks);
      readClipboard();
    } else {
      setRaw('');
      setResult(null);
      setError('');
    }
  }, [open, hasBlocks, readClipboard]);

  const s = result?.stats;
  const rehostCount = result ? collectRehostTasks(result.blocks).length : 0;
  const summary = s
    ? [
        s.headings && `заголовков: ${s.headings}`,
        s.paragraphs && `текстов: ${s.paragraphs}`,
        s.images && `картинок: ${s.images}`,
        s.videos && `видео: ${s.videos}`,
        s.videosMissing && `видео без ссылки: ${s.videosMissing}`,
        s.audios && `аудио: ${s.audios}`,
        s.buttons && `кнопок: ${s.buttons}`,
        s.files && `файлов: ${s.files}`,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Вставить урок из GetCourse</DialogTitle>
      <DialogContent dividers>
        {!result && (
          <>
            <p style={{ marginTop: 0 }}>
              На странице урока GetCourse нажмите «Скопировать в MUIP» (расширение),
              затем вернитесь сюда.
            </p>
            <Button variant="outlined" onClick={readClipboard} sx={{ mb: 2 }}>
              Прочитать буфер обмена
            </Button>
            <TextField
              label="…или вставьте сюда (Ctrl/Cmd+V)"
              value={raw}
              onChange={(e) => apply(e.target.value)}
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
            />
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {result && (
          <>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {result.title || 'Без названия'}
            </div>
            <div style={{ color: '#666', marginBottom: 12 }}>
              {summary || 'Контента не найдено'}
              {result.assignmentText && ', есть задание'}
            </div>

            {result.warnings.map((w) => (
              <Alert severity="warning" sx={{ mb: 1 }} key={w}>
                {w}
              </Alert>
            ))}
            {rehostCount > 0 && (
              <Alert severity="info" sx={{ mb: 1 }}>
                После вставки перенесём в наше хранилище картинки, аудио и файлы с
                GetCourse ({rehostCount}). Большое аудио может качаться пару минут.
              </Alert>
            )}

            {hasBlocks && (
              <RadioGroup
                value={replaceBlocks ? 'replace' : 'append'}
                onChange={(e) => setReplaceBlocks(e.target.value === 'replace')}
              >
                <FormControlLabel value="append" control={<Radio />} label="Добавить в конец урока" />
                <FormControlLabel value="replace" control={<Radio />} label="Заменить текущие блоки" />
              </RadioGroup>
            )}
            {result.title && (
              <FormControlLabel
                control={<Checkbox checked={useTitle} onChange={(e) => setUseTitle(e.target.checked)} />}
                label={`Название урока: «${result.title}»`}
              />
            )}
            {result.assignmentText && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createAssignment}
                    onChange={(e) => setCreateAssignment(e.target.checked)}
                  />
                }
                label="Создать черновик ДЗ из задания (не опубликован)"
              />
            )}
            {result.assignmentText && createAssignment && (
              <RadioGroup
                row
                sx={{ pl: 4 }}
                value={assignmentAnswer}
                onChange={(e) => setAssignmentAnswer(e.target.value as AssignmentAnswerType)}
              >
                <FormControlLabel value="free_text" control={<Radio size="small" />} label="Ответ текстом" />
                <FormControlLabel value="file_upload" control={<Radio size="small" />} label="Ответ файлом" />
              </RadioGroup>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result && (
          <Button onClick={() => apply('')} sx={{ mr: 'auto' }}>
            Другие данные
          </Button>
        )}
        <Button onClick={onClose}>Отмена</Button>
        <Button
          variant="contained"
          disabled={!result || result.blocks.length === 0}
          onClick={() =>
            result &&
            onImport(result, {
              replaceBlocks: replaceBlocks || !hasBlocks,
              useTitle,
              createAssignment,
              assignmentAnswer,
            })
          }
        >
          Вставить
        </Button>
      </DialogActions>
    </Dialog>
  );
};
